import { useCallback, useEffect } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { checkIsHashActive } from './check-is-hash-active'

interface UseRouteHashProps {
    hash?: string
    onHashRemoved?: () => void
    replace?: boolean
    onHashAdded?: () => void
}

function getPathWithSearch(pathname: string, search: string) {
    return search ? `${pathname}?${search}` : pathname
}

export function useRouteHash({ hash, onHashRemoved, replace, onHashAdded }: UseRouteHashProps) {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const search = searchParams.toString()

    const addHash = useCallback(() => {
        if (!hash) return

        if (checkIsHashActive(hash)) return

        const pathWithSearch = getPathWithSearch(pathname, search)
        const urlWithHash = `${pathWithSearch}#${hash}`
        if (replace) {
            router.replace(urlWithHash, { scroll: false })
        } else {
            router.push(urlWithHash, { scroll: false })
        }
    }, [hash, pathname, replace, router, search])

    const removeHash = useCallback(() => {
        if (!hash) return

        const isHashActive = checkIsHashActive(hash)

        if (!isHashActive) return

        const pathWithSearch = getPathWithSearch(pathname, search)
        router.replace(pathWithSearch, { scroll: false })
    }, [hash, pathname, router, search])

    useEffect(() => {
        if (!hash) return

        if ((!onHashRemoved && !onHashAdded) || typeof window === 'undefined') return

        const handleHashChange = () => {
            const currentUrlHash = window.location.hash
            const isCurrentHashActive = currentUrlHash === `#${hash}`

            if (!isCurrentHashActive) {
                onHashRemoved?.()
            }

            if (isCurrentHashActive) {
                onHashAdded?.()
            }
        }

        window.addEventListener('hashchange', handleHashChange)

        return () => window.removeEventListener('hashchange', handleHashChange)
    }, [hash, onHashRemoved, onHashAdded])

    return { addHash, removeHash }
}
